/** external */
import _ from "lodash";
import PropTypes from "prop-types";
import classNames from "classnames";
import update from "immutability-helper";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { TextAreaInput } from "@toddle-edu/ds-web";
import { graphql, compose } from "lib/apolloClient";
import React, { useMemo, useEffect, useState, useRef } from "react";

/** internal */
import I18nHOC from "UIComponents/I18nHOC";
import { generateRandomId } from "Utils";
import { getIsFromClassroom } from "UnitPlans/modules/Utils";
import { getAssessmentQuery } from "Assessments/modules/AssessmentQuery";
import { getPlannerTemplateFromCache } from "modules/CommonGraphqlHelpers";
import { updateGeneratingAIQuestions } from "Worksheet/modules/WorksheetModules";
import {
  FIELD_UID_ADD_LEARNING_GOALS,
  MODE_TEACHER_CREATE,
} from "Worksheet/modules/WorksheetConstants";
import { getAddLearningGoalsDropdownOptions } from "UnitPlans/components/LibraryCommonComponents/ResourceEdit/modules/Utils";
import {
  getLearningGoalsContext,
  getTemplateArray,
} from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/Utils";
import {
  getOptionsFromField,
  replaceLearningGoalsFields,
} from "Worksheet/modules/WorksheetUtils";
import {
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";
import {
  updateAIConversation,
  getVariablesForLocalisedPlannerTemplates,
} from "modules/Services";
import { getResolvedOptimisticValue } from "modules/ElementValueUtils";
import {
  getUserType,
  getSelectedCurriculumId,
  getSelectedAcademicYearId,
  getCurrentCurriculumProgramType,
} from "modules/CommonSelectors";
import {
  getFieldValue,
  getDynamicTemplate,
  updateAssessmentTemplate,
  getUnitPlanFromAssessment,
  combineUnitPlanMultiFieldData,
  getUnitDetailsFromUnitPlanField,
  changeDisciplinaryContentToMultiField,
} from "Assessments/modules/Utils";

/** relative */
import classes from "./LearningManager.scss";
import LearningGoalFields from "./LearningGoalFields";
import { SYLLABUS_CONTENT_DP } from "../../AiQuestionGenerator/AiQuestionGeneratorConstants";
import {
  getTargetedContentField,
  getIntialLearningGoalsData,
} from "../../AiQuestionGenerator/AiQuestionGeneratorUtils";

/**
 *
 * @param {*} props
 * @returns LearningManager component
 * @description This component is used to manage learning goals and targeted content for AI-generated questions and Extract from Doc
 */
const LearningManager = props => {
  const {
    courseId,
    fieldList,
    templateId,
    portalType,
    curriculumType,
    assessmentType,
    assessmentTools,
    assessmentDetails,
    originalTemplate,
    unitPlanFieldList,
    learningGoalsFields,
    learningGoalOptions,
    conversationMetadata,
    learningGoalsFieldsUIDs,

    // * Props From Parent component
    context,
    setContext,
    isExtractFromDoc,
    onChangeLearningGoals,
    onChangeTargetedContent,
    setUpdatedAssessmentAndUnitPlanDetails,
    unitPlanDetails,
    assessmentTemplate,
    isFromWorksheetAIPanel,
    renderTitle,
    renderValue,
    isLoading,
  } = props;
  const { t } = useTranslation();

  // * For AI-generated questions, there will be saved meta data and no initial learning goals data for extract from doc
  const [learningGoalsFieldsData, setLearningGoalsFieldsData] = useState([]);
  const initialLearningGoalsUpdated = useRef(false);

  // * Making callback to parent component to update initial learningGoalsFieldsData
  useEffect(() => {
    const shouldSkip =
      isExtractFromDoc ||
      _.isEmpty(conversationMetadata) ||
      _.isEmpty(learningGoalsFieldsUIDs) ||
      initialLearningGoalsUpdated.current ||
      isLoading;

    if (shouldSkip) return;

    initialLearningGoalsUpdated.current = true;
    const initialLearningGoalsData = getIntialLearningGoalsData({
      conversationMetadata,
      learningGoalsFieldsUIDs,
    });
    setLearningGoalsFieldsData(initialLearningGoalsData);

    const payload = { newLearningGoalsFieldData: initialLearningGoalsData };
    onChangeLearningGoals(payload);
  }, [
    isExtractFromDoc,
    conversationMetadata,
    learningGoalsFieldsUIDs,
    isLoading,
  ]);

  // * Should show targeted content and getting target content field key
  const { showTargetedContent, TARGETED_CONTENT } = useMemo(() => {
    const TARGETED_CONTENT = getTargetedContentField(curriculumType);

    const showTargetedContent = isExtractFromDoc
      ? false
      : curriculumType === CURRICULUM_TYPE_MYP ||
        (curriculumType === CURRICULUM_TYPE_DP &&
          !_.some(learningGoalOptions, { key: SYLLABUS_CONTENT_DP }));

    return { showTargetedContent, TARGETED_CONTENT };
  }, [curriculumType, learningGoalOptions]);

  // * Updating targeted content in context and make a callback to parent component to update in API
  const updateTargetedContent = e => {
    const value = e.target.value;

    if (_.isFunction(onChangeTargetedContent)) {
      onChangeTargetedContent(TARGETED_CONTENT, value);
    } else {
      const updatedContext = { ...context, [TARGETED_CONTENT]: value };
      if (_.isFunction(setContext)) setContext(updatedContext);
    }
  };

  // * Updating learning goals fields data and unit plan fields data in context and make a callback to parent component to update in API
  const { updatedAssessmentDetails, updatedUnitPlanDetails } = useMemo(() => {
    const updatedAssessmentFields = replaceLearningGoalsFields({
      template: originalTemplate,
      replacementFields: learningGoalsFieldsData,
      fields: _.get(assessmentDetails, "allFields", []),
    });

    const updatedAssessmentDetails = update(assessmentDetails, {
      allFields: { $set: updatedAssessmentFields },
    });

    const unitPlanFields = _.get(unitPlanDetails, "unitPlanFields", []);
    const combinedFields = combineUnitPlanMultiFieldData({
      unitPlanFields: _.cloneDeep(unitPlanFields),
      templateBody: assessmentTemplate,
    });
    const updatedUnitPlanDetails = {
      ...unitPlanDetails,
      unitPlanFields: _.uniqBy(
        [...(combinedFields || []), ...(unitPlanFields || [])],
        "uid"
      ),
    };

    return { updatedAssessmentDetails, updatedUnitPlanDetails };
  }, [
    unitPlanDetails,
    originalTemplate,
    assessmentDetails,
    assessmentTemplate,
    learningGoalsFieldsData,
  ]);

  // * Updating updatedAssessmentDetails and updatedUnitPlanDetails in context and make a callback to parent component to update in API
  useEffect(() => {
    if (!_.isFunction(setUpdatedAssessmentAndUnitPlanDetails)) return;
    const payload = { updatedAssessmentDetails, updatedUnitPlanDetails };
    setUpdatedAssessmentAndUnitPlanDetails(payload);
  }, [
    JSON.stringify(updatedAssessmentDetails || "{}"),
    JSON.stringify(updatedUnitPlanDetails || "{}"),
  ]);

  /**
   *
   * @param {*} args
   * @description
   * Updating learning goals fields data in context and make a callback to parent component to update in API
   * Making callback for Extract from Doc/Forms and updating local state
   */
  const handleOnUpdateField = args => {
    const { params, extraParams } = args;
    const fieldUID = _.first(_.keys(params));
    const payload = { uid: fieldUID };

    const fieldValue = _.get(params, fieldUID, []);

    /**
     * Use functional state update because React batches state updates
     * This ensures previous learning goals are not overwritten,
     * when multiple fields are updated in quick succession
     */
    setLearningGoalsFieldsData(prev => {
      const newLearningGoalsFieldData = _.isArray(prev) ? [...prev] : [];

      const fieldData = _.find(newLearningGoalsFieldData, payload);

      const resolvedMinimalTree = getResolvedOptimisticValue({
        fieldUID,
        value: fieldValue,
        params: extraParams,
        fieldTemplateObj: _.get(fieldList, fieldUID, {}),
        resolvedValue: _.get(fieldData, "resolvedMinimalTree", {}),
      });

      const newOrUpdatedFieldData = {
        uid: fieldUID,
        value: fieldValue,
        resolvedMinimalTree,
        id: _.get(fieldData, "id", generateRandomId()),
      };

      if (_.isEmpty(fieldData)) {
        // * Adding new or updated field data in learning goals
        newLearningGoalsFieldData.push(newOrUpdatedFieldData);
      } else if (_.isEmpty(fieldValue)) {
        // * Removing field from learning goals
        const fieldIndex = _.findIndex(newLearningGoalsFieldData, payload);
        if (fieldIndex > -1) newLearningGoalsFieldData.splice(fieldIndex, 1);
      } else {
        // * Updating field data in learning goals
        const fieldIndex = _.findIndex(newLearningGoalsFieldData, payload);
        newLearningGoalsFieldData[fieldIndex] = newOrUpdatedFieldData;
      }

      // * Callback For Extract from Doc/Forms and AI question generator
      if (isExtractFromDoc) {
        const learningGoals = getLearningGoalsContext({
          questionText: "",
          learningGoalsFields: learningGoalsFields,
          unitPlanFields: _.get(updatedUnitPlanDetails, "unitPlanFields", []),
          conversationMetadata: { allFields: newLearningGoalsFieldData },
        });
        onChangeLearningGoals({ learningGoals, newLearningGoalsFieldData });
      } else {
        onChangeLearningGoals({
          newLearningGoalsFieldData: newLearningGoalsFieldData,
        });
      }

      return newLearningGoalsFieldData;
    });
  };

  const targetedContentClasses = classNames(
    classes.targetedContentText,
    "text-label",
    classes.textDefault
  );

  const learningManagerClasses = classNames({
    [classes.learningManagerComponent]: !isFromWorksheetAIPanel,
  });

  return (
    <div
      role="dialog"
      aria-labelledby="worksheetAIHeader"
      aria-describedby="worksheetAIDescription"
      className={learningManagerClasses}
    >
      {showTargetedContent && (
        <div className={classes.targetedContentContainer}>
          <div
            htmlFor="targetedContentInput"
            className={targetedContentClasses}
          >
            {t("ai:targeted_content")}
          </div>
          <TextAreaInput
            id="targetedContentInput"
            onChange={updateTargetedContent}
            value={context[TARGETED_CONTENT]}
            className={classes.targetedContentInput}
            placeholder={t("worksheet:enter_targeted_content")}
          />
        </div>
      )}

      <LearningGoalFields
        courseId={courseId}
        fieldList={fieldList}
        portalType={portalType}
        templateId={templateId}
        assessmentType={assessmentType}
        curriculumType={curriculumType}
        assessmentTools={assessmentTools}
        unitPlanFieldList={unitPlanFieldList}
        assessmentTemplate={assessmentTemplate}
        learningGoalOptions={learningGoalOptions}
        handleOnUpdateField={handleOnUpdateField}
        learningGoalFields={learningGoalsFieldsData}
        assessmentFields={_.get(updatedAssessmentDetails, "allFields", [])}
        unitPlanFields={_.get(updatedUnitPlanDetails, "unitPlanFields", [])}
        isFromWorksheetAIPanel={isFromWorksheetAIPanel}
        renderTitle={renderTitle}
        renderValue={renderValue}
      />
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const curriculumType = getCurrentCurriculumProgramType(state);
  const academicYearId = getSelectedAcademicYearId(state);

  const portalType = _.get(state, "login.userInfo.portalType");
  const collaboratorsFilter = { userAcademicYearId: academicYearId };

  const courseId =
    _.get(ownProps, "courseId") ||
    _.get(ownProps, "selectedCourse.courseId") ||
    _.get(state, "teacher.selected_class.selected_course");

  return {
    courseId,
    portalType,
    curriculumType,
    collaboratorsFilter,
    userType: getUserType(state),
    curriculumId: getSelectedCurriculumId(state),
    mode: _.get(ownProps, "mode", MODE_TEACHER_CREATE),
    plannerElementSets: _.get(state, "platform.currentPlannerElementSets", []),
  };
};

const mapActionCreators = {
  updateAIConversation,
  updateGeneratingAIQuestions,
};

LearningManager.propTypes = {
  toggleAiQuestionGenerator: PropTypes.func,
  isExtractFromDoc: PropTypes.bool,
  onChangeLearningGoals: PropTypes.func,
  onChangeTargetedContent: PropTypes.func,
  isFromWorksheetAIPanel: PropTypes.bool,
};

LearningManager.defaultProps = {
  toggleAiQuestionGenerator: _.noop,
  isExtractFromDoc: false,
  onChangeLearningGoals: _.noop,
  onChangeTargetedContent: _.noop,
  isFromWorksheetAIPanel: false,
};

export default compose(
  I18nHOC({ resource: ["ai", "common"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getAssessmentQuery, {
    name: "getAssessment",
    options: ({ assessmentId, portalType, collaboratorsFilter }) => ({
      // cache-first is used because this query will always get called in AssessmentDetails.js and latest data will be available in cache
      // if we use cache-and-network, it will make a network call and override the cache data
      // the reason to not use getAssessmentDetailsFromCache directly is this component won't get re-rendered if cache data changes
      fetchPolicy: "cache-first",
      variables: { id: assessmentId, collaboratorsFilter, portalType },
    }),
    props({
      getAssessment,
      ownProps: {
        t,
        mode,
        sourceFrom,
        curriculumType,
        unitPlanId,
        plannerElementSets,
        i18n,
        classSubjects,
        userType,
        curriculumId,
        creationMode,
        isLoading,
      },
    }) {
      const assessmentDetails = _.get(getAssessment, "node", {});
      const assessmentTools = _.get(
        assessmentDetails,
        "assessmentTools.edges",
        []
      );

      const assessmentType = _.get(assessmentDetails, "assessmentType.value");
      const templateId = _.get(assessmentDetails, "templateId", null);
      const unitPlan = getUnitPlanFromAssessment({ assessmentDetails });
      const unitPlanTemplateId = _.get(unitPlan, "templateId", null);
      const presetAssessment = _.get(assessmentDetails, "presetAssessment", {});
      const assessmentObj = presetAssessment || assessmentDetails;
      const unitPlanDetails = getUnitDetailsFromUnitPlanField({
        assessmentObj,
        assessmentDetails,
        ownPropsUnitPlanId: unitPlanId,
      });
      const assessmentFields = _.get(assessmentDetails, "allFields", []);

      const { unitPlanFields } = unitPlanDetails || {};

      const unitType = getFieldValue({
        fields: unitPlanFields,
        uid: "unitType",
      });

      const resolvedSubjectNodes = _.get(
        _.find(unitPlanFields, { uid: "subjects" }),
        "resolvedMinimalTree.subjects",
        []
      );

      let templates = getPlannerTemplateFromCache(
        { ids: getTemplateArray([templateId]) },
        true
      );

      if (mode !== "view" && !_.isEmpty(templates)) {
        const unitPlanTemplate = getPlannerTemplateFromCache(
          getVariablesForLocalisedPlannerTemplates({
            templates: [unitPlanTemplateId],
            language: i18n.language,
          })
        );
        templates = [...templates, unitPlanTemplate];
      }

      if (_.isEmpty(templates)) {
        templates = getPlannerTemplateFromCache(
          { ids: getTemplateArray([templateId]) },
          true
        );
      }

      const originalTemplate = _.get(
        _.find(templates, { id: templateId }),
        "body",
        {}
      );

      const isFromClassroom = getIsFromClassroom({
        sourceFrom,
        template: originalTemplate,
      });

      const dynamicTemplate = getDynamicTemplate({
        template: originalTemplate,
        unitType,
        t,
        curriculumType,
        plannerElementSets,
        subjects: classSubjects,
        creationMode,
        curriculumId,
      });

      const assessmentTemplate = updateAssessmentTemplate({
        t,
        template: dynamicTemplate,
        assessmentType,
        isFromClassroom,
        curriculumType,
        resourceFields: assessmentFields,
        mode: "edit",
        templateId,
        plannerElementSets,
        subjects: classSubjects,
        userType,
        assessmentTools: _.get(assessmentDetails, "assessmentTools", []),
      });

      let _unitPlanTemplate = _.find(templates, { id: unitPlanTemplateId });

      if (_.isEmpty(_unitPlanTemplate) && !_.isEmpty(unitPlanTemplateId)) {
        _unitPlanTemplate = getPlannerTemplateFromCache(
          getVariablesForLocalisedPlannerTemplates({
            templates: [unitPlanTemplateId],
            language: i18n.language,
          })
        );
      }

      const disciplinaryContentToMultiField = changeDisciplinaryContentToMultiField(
        {
          t,
          curriculumType,
          resolvedSubjectNodes,
          template: _unitPlanTemplate,
          unitType: getFieldValue({
            fields: _.get(unitPlanDetails, "unitPlanFields", []),
            uid: "unitType",
          }),
        }
      );

      const unitPlanTemplate = _.get(
        disciplinaryContentToMultiField,
        "body",
        {}
      );

      const unitPlanFieldList = _.get(unitPlanTemplate, "field_list", {});
      const fieldList = _.get(assessmentTemplate, "field_list", {});

      const defaultOptions = getOptionsFromField({
        t,
        fieldList,
        fieldUid: FIELD_UID_ADD_LEARNING_GOALS,
      });

      const learningGoalOptions = getAddLearningGoalsDropdownOptions({
        options: defaultOptions,
        resourceFields: assessmentFields,
        unitPlanTemplate,
        subjects: classSubjects,
        fieldList,
      });

      const learningGoalsFieldsUIDs = _.map(learningGoalOptions, "key");
      const learningGoalsFields = _.pick(fieldList, learningGoalsFieldsUIDs);
      const networkStatus = _.get(getAssessment, "networkStatus");

      return {
        fieldList,
        templateId,
        assessmentType,
        assessmentTools,
        originalTemplate,
        assessmentFields,
        assessmentDetails,
        unitPlanFieldList,
        learningGoalOptions,
        learningGoalsFields,
        learningGoalsFieldsUIDs,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  })
)(LearningManager);
